import React, { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, CheckCircleIcon, PlayIcon, TrashIcon, ArrowLeftIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import UpgradePrompt from '../components/UpgradePrompt';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

const QUESTIONS_BY_LANGUAGE = {
  en: [
    { id: 1, text: "What's a lesson or piece of advice you want to pass on to future generations?", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "Tell me about someone who had a profound impact on your life and what you learned from them", maxDuration: 120 }
  ],
  es: [
    { id: 1, text: "¿Cuál es una lección o consejo que quieres transmitir a las futuras generaciones?", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "Cuéntame sobre alguien que tuvo un impacto profundo en tu vida y qué aprendiste de esa persona", maxDuration: 120 }
  ],
  fr: [
    { id: 1, text: "Quelle leçon ou conseil souhaitez-vous transmettre aux générations futures?", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "Parlez-moi de quelqu'un qui a eu un impact profond sur votre vie et de ce que vous avez appris de cette personne", maxDuration: 120 }
  ],
  de: [
    { id: 1, text: "Welche Lektion oder welchen Rat möchten Sie an zukünftige Generationen weitergeben?", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "Erzählen Sie mir von jemandem, der einen tiefgreifenden Einfluss auf Ihr Leben hatte und was Sie von dieser Person gelernt haben", maxDuration: 120 }
  ],
  zh: [
    { id: 1, text: "你想传递给后代的教训或建议是什么？", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "告诉我一个对你的生活产生深远影响的人，以及你从他们身上学到了什么", maxDuration: 120 }
  ],
  hi: [
    { id: 1, text: "आप भविष्य की पीढ़ियों को कौन सा सबक या सलाह देना चाहते हैं?", maxDuration: 120, minDuration: 60 },
    { id: 2, text: "मुझे किसी ऐसे व्यक्ति के बारे में बताएं जिसने आपके जीवन पर गहरा प्रभाव डाला और आपने उनसे क्या सीखा", maxDuration: 120 }
  ]
};

const ADDITIONAL_QUESTIONS_BY_LANGUAGE = {
  en: [
    { id: 3, text: "What's a defining moment or turning point in your life that shaped who you are today?", maxDuration: 120 },
    { id: 4, text: "What are the core values that have guided your life decisions?", maxDuration: 120 },
    { id: 5, text: "Tell me about your family - what do you want them to know about your love for them?", maxDuration: 120 },
    { id: 6, text: "What's a challenge you overcame and what did it teach you?", maxDuration: 120 },
    { id: 7, text: "What accomplishment are you most proud of and why?", maxDuration: 120 },
    { id: 8, text: "If you could give your younger self one piece of advice, what would it be?", maxDuration: 120 },
    { id: 9, text: "What brings you the most joy and fulfillment in life?", maxDuration: 120 },
    { id: 10, text: "What do you hope your legacy will be? How do you want to be remembered?", maxDuration: 120 },
    { id: 11, text: "Is there a story from your life that you want to make sure is never forgotten?", maxDuration: 120 }
  ],
  es: [
    { id: 3, text: "¿Cuál fue un momento decisivo en tu vida que te formó como persona?", maxDuration: 120 },
    { id: 4, text: "¿Cuáles son los valores fundamentales que han guiado tus decisiones de vida?", maxDuration: 120 },
    { id: 5, text: "Háblame de tu familia - ¿qué quieres que sepan sobre tu amor por ellos?", maxDuration: 120 },
    { id: 6, text: "¿Qué desafío superaste y qué te enseñó?", maxDuration: 120 },
    { id: 7, text: "¿De qué logro te sientes más orgulloso y por qué?", maxDuration: 120 },
    { id: 8, text: "Si pudieras darle un consejo a tu yo más joven, ¿cuál sería?", maxDuration: 120 },
    { id: 9, text: "¿Qué te trae más alegría y satisfacción en la vida?", maxDuration: 120 },
    { id: 10, text: "¿Cuál esperas que sea tu legado? ¿Cómo quieres ser recordado?", maxDuration: 120 },
    { id: 11, text: "¿Hay alguna historia de tu vida que quieras asegurarte de que nunca se olvide?", maxDuration: 120 }
  ],
  fr: [
    { id: 3, text: "Quel est un moment déterminant de votre vie qui a façonné qui vous êtes aujourd'hui?", maxDuration: 120 },
    { id: 4, text: "Quelles sont les valeurs fondamentales qui ont guidé vos décisions de vie?", maxDuration: 120 },
    { id: 5, text: "Parlez-moi de votre famille - que voulez-vous qu'ils sachent sur votre amour pour eux?", maxDuration: 120 },
    { id: 6, text: "Quel défi avez-vous surmonté et qu'est-ce que cela vous a appris?", maxDuration: 120 },
    { id: 7, text: "De quelle réalisation êtes-vous le plus fier et pourquoi?", maxDuration: 120 },
    { id: 8, text: "Si vous pouviez donner un conseil à votre jeune moi, quel serait-il?", maxDuration: 120 },
    { id: 9, text: "Qu'est-ce qui vous apporte le plus de joie et d'épanouissement dans la vie?", maxDuration: 120 },
    { id: 10, text: "Quel espérez-vous que soit votre héritage? Comment voulez-vous être rappelé?", maxDuration: 120 },
    { id: 11, text: "Y a-t-il une histoire de votre vie que vous voulez vous assurer ne soit jamais oubliée?", maxDuration: 120 }
  ],
  de: [
    { id: 3, text: "Was war ein entscheidender Moment in Ihrem Leben, der Sie zu dem gemacht hat, was Sie heute sind?", maxDuration: 120 },
    { id: 4, text: "Welche Grundwerte haben Ihre Lebensentscheidungen geleitet?", maxDuration: 120 },
    { id: 5, text: "Erzählen Sie mir von Ihrer Familie - was möchten Sie, dass sie über Ihre Liebe zu ihnen wissen?", maxDuration: 120 },
    { id: 6, text: "Welche Herausforderung haben Sie überwunden und was hat sie Sie gelehrt?", maxDuration: 120 },
    { id: 7, text: "Auf welche Leistung sind Sie am meisten stolz und warum?", maxDuration: 120 },
    { id: 8, text: "Wenn Sie Ihrem jüngeren Ich einen Rat geben könnten, welcher wäre das?", maxDuration: 120 },
    { id: 9, text: "Was bringt Ihnen die größte Freude und Erfüllung im Leben?", maxDuration: 120 },
    { id: 10, text: "Was hoffen Sie, wird Ihr Vermächtnis sein? Wie möchten Sie in Erinnerung bleiben?", maxDuration: 120 },
    { id: 11, text: "Gibt es eine Geschichte aus Ihrem Leben, von der Sie sicherstellen möchten, dass sie nie vergessen wird?", maxDuration: 120 }
  ],
  zh: [
    { id: 3, text: "你生命中的一个决定性时刻或转折点是什么，它塑造了今天的你？", maxDuration: 120 },
    { id: 4, text: "指导你人生决策的核心价值观是什么？", maxDuration: 120 },
    { id: 5, text: "谈谈你的家人——你想让他们知道你对他们的爱是什么？", maxDuration: 120 },
    { id: 6, text: "你克服了什么挑战，它教会了你什么？", maxDuration: 120 },
    { id: 7, text: "你最引以为豪的成就是什么，为什么？", maxDuration: 120 },
    { id: 8, text: "如果你能给年轻时的自己一个建议，会是什么？", maxDuration: 120 },
    { id: 9, text: "生活中什么给你带来最大的快乐和满足？", maxDuration: 120 },
    { id: 10, text: "你希望你的遗产是什么？你希望如何被人记住？", maxDuration: 120 },
    { id: 11, text: "你生命中有没有一个你想确保永远不会被遗忘的故事？", maxDuration: 120 }
  ],
  hi: [
    { id: 3, text: "आपके जीवन का वह निर्णायक क्षण या मोड़ क्या था जिसने आपको आज जो आप हैं वह बनाया?", maxDuration: 120 },
    { id: 4, text: "वे मूल मूल्य क्या हैं जिन्होंने आपके जीवन के निर्णयों का मार्गदर्शन किया है?", maxDuration: 120 },
    { id: 5, text: "मुझे अपने परिवार के बारे में बताएं - आप उन्हें अपने प्यार के बारे में क्या जानना चाहते हैं?", maxDuration: 120 },
    { id: 6, text: "आपने किस चुनौती को पार किया और इसने आपको क्या सिखाया?", maxDuration: 120 },
    { id: 7, text: "आपको किस उपलब्धि पर सबसे अधिक गर्व है और क्यों?", maxDuration: 120 },
    { id: 8, text: "यदि आप अपने छोटे स्व को एक सलाह दे सकते हैं, तो वह क्या होगी?", maxDuration: 120 },
    { id: 9, text: "जीवन में आपको सबसे अधिक खुशी और संतुष्टि क्या देता है?", maxDuration: 120 },
    { id: 10, text: "आप क्या उम्मीद करते हैं कि आपकी विरासत क्या होगी? आप कैसे याद किए जाना चाहते हैं?", maxDuration: 120 },
    { id: 11, text: "क्या आपके जीवन की कोई कहानी है जिसे आप सुनिश्चित करना चाहते हैं कि कभी भुलाया न जाए?", maxDuration: 120 }
  ]
};

export default function PodcastStudio() {
  const { user } = useAuth();
  const { subscription, usage, hasReachedLimit, checkLimit, incrementUsage } = useSubscription();
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recordings, setRecordings] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [userLanguage, setUserLanguage] = useState('en');
  const [podcastUrl, setPodcastUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Load saved session and language preference on mount
  useEffect(() => {
    if (user) {
      loadSession();
      loadLanguagePreference();
    }
  }, [user]);

  // Initialize questions when language is loaded
  useEffect(() => {
    if (userLanguage) {
      const initialQuestions = QUESTIONS_BY_LANGUAGE[userLanguage] || QUESTIONS_BY_LANGUAGE.en;
      setQuestions(initialQuestions);
    }
  }, [userLanguage]);

  const loadLanguagePreference = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/user/${user.id}/language-preference`);
      const langCode = response.data.language_code || 'en';
      setUserLanguage(langCode);
    } catch (error) {
      console.log('Failed to load language preference, using English');
      setUserLanguage('en');
    }
  };

  const loadSession = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/podcast/progress/${user.id}`);
      if (response.data.session && response.data.session.recordings) {
        setSessionId(response.data.session.id);
        
        // Convert recordings array to object keyed by question_number
        const recordingsObj = {};
        response.data.session.recordings.forEach(rec => {
          recordingsObj[rec.question_number] = {
            audioPath: rec.audio_path,
            duration: rec.duration,
            questionText: rec.question_text
          };
        });
        setRecordings(recordingsObj);
      }
    } catch (error) {
      console.log('No saved session found, starting fresh');
    }
  };

  const startRecording = async () => {
    // Check podcast session limit before starting
    if (!sessionId) {
      const limitCheck = await checkLimit('podcast_sessions');
      if (!limitCheck.allowed) {
        setShowUpgradePrompt(true);
        return;
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await saveRecording(blob);
        stream.getTracks().forEach(track => track.stop());
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTimer(0);

      const maxDuration = questions[currentQuestionIndex].maxDuration;
      
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);

      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, maxDuration * 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const saveRecording = async (audioBlob) => {
    try {
      const questionNumber = currentQuestionIndex + 1;
      const question = questions[currentQuestionIndex];
      
      // Upload audio file
      const formData = new FormData();
      formData.append('file', audioBlob, `q${questionNumber}.webm`);
      const uploadResponse = await axios.post(`${API_URL}/upload-audio`, formData);
      
      if (!user) {
        alert('Please log in to save your recording');
        return;
      }
      
      // Save to database
      const saveResponse = await axios.post(`${API_URL}/podcast/save-recording`, {
        user_id: user.id,
        session_id: sessionId,
        question_number: questionNumber,
        question_text: question.text,
        audio_path: uploadResponse.data.file_path,
        duration: timer
      });
      
      // Update session_id if new and increment usage
      if (saveResponse.data.session_id && !sessionId) {
        setSessionId(saveResponse.data.session_id);
        // Increment podcast session usage counter (only once per session)
        await incrementUsage('podcast_sessions', 1);
      }
      
      // Update local state
      setRecordings(prev => ({
        ...prev,
        [questionNumber]: {
          audioBlob: audioBlob,
          audioPath: uploadResponse.data.file_path,
          duration: timer,
          questionText: question.text
        }
      }));
      
      // Clone voice after Question 1
      if (questionNumber === 1 && timer >= 60) {
        await cloneVoice(audioBlob, uploadResponse.data.file_path);
      }
      
      setTimer(0);
    } catch (error) {
      console.error('Failed to save recording:', error);
      alert('Failed to save recording. Please try again.');
    }
  };

  const cloneVoice = async (audioBlob, audioPath) => {
    try {
      console.log('Cloning voice from Question 1...');
      
      if (!user) return;
      
      // Clone voice using ElevenLabs
      const cloneResponse = await axios.post(`${API_URL}/voice/clone`, {
        user_id: user.id,
        audio_url: audioPath,
        voice_name: `${user.name || 'User'}'s Voice`,
        description: 'Voice clone from podcast Question 1'
      });
      
      const voiceId = cloneResponse.data.voice_id;
      
      // Save voice_id to database
      await axios.post(`${API_URL}/podcast/save-voice`, {
        user_id: user.id,
        voice_id: voiceId,
        session_id: sessionId
      });
      
      // Generate preview message
      const previewResponse = await axios.post(`${API_URL}/voice/generate`, {
        text: "Happy birthday! I'm so proud of you and everything you've accomplished. I love you.",
        voice_id: voiceId,
        user_id: user.id
      });
      
      // Save preview audio URL to session
      await axios.post(`${API_URL}/podcast/save-voice`, {
        user_id: user.id,
        voice_id: voiceId,
        session_id: sessionId,
        preview_audio_url: previewResponse.data.audio_url
      });
      
      alert('✓ Voice cloned successfully! You can now use "Come Alive" to generate messages in your voice.');
      
    } catch (error) {
      console.error('Failed to clone voice:', error);
      alert('Voice cloning failed. You can still continue recording, but messages will use a default voice.');
    }
  };

  const deleteRecording = async (questionNumber) => {
    if (!window.confirm('Delete this recording?')) return;
    
    const newRecordings = { ...recordings };
    delete newRecordings[questionNumber];
    setRecordings(newRecordings);
    
    // TODO: Add backend endpoint to delete recording from session
  };

  const playRecording = (questionNumber) => {
    const recording = recordings[questionNumber];
    if (!recording) return;
    
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }
    
    const audio = new Audio();
    if (recording.audioBlob) {
      audio.src = URL.createObjectURL(recording.audioBlob);
    } else if (recording.audioPath) {
      audio.src = `${API_URL}${recording.audioPath}`;
    }
    
    audio.play();
    setIsPlaying(questionNumber);
    
    audio.onended = () => setIsPlaying(null);
    audioPlayerRef.current = audio;
  };

  const compilePodcast = async () => {
    setCompiling(true);
    
    try {
      const audioSegments = Object.entries(recordings).map(([questionNumber, recording]) => ({
        type: 'answer',
        path: recording.audioPath,
        question_text: recording.questionText,
        question_number: parseInt(questionNumber)
      }));
      
      if (!user) {
        alert('Please log in to compile your podcast');
        return;
      }
      
      const response = await axios.post(`${API_URL}/podcast/compile`, {
        user_id: user.id,
        session_id: sessionId,
        audio_segments: audioSegments
      });
      
      console.log('Podcast compilation response:', response.data);
      console.log('Podcast URL:', response.data.podcast_url);
      setPodcastUrl(response.data.podcast_url);
      setShowShareModal(true);
      
    } catch (error) {
      console.error('Failed to compile podcast:', error);
      alert('Failed to compile podcast: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCompiling(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateQuestion = (id, newText) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, text: newText } : q));
    setEditingQuestionId(null);
    setEditText('');
  };

  const addCustomQuestion = () => {
    if (!editText.trim()) return;
    const newId = Math.max(...questions.map(q => q.id)) + 1;
    setQuestions(prev => [...prev, { id: newId, text: editText, maxDuration: 120 }]);
    setEditText('');
  };

  const deleteQuestion = (id) => {
    if (questions.length <= 1) {
      alert('You must have at least one question');
      return;
    }
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const addMoreQuestions = () => {
    const currentMaxId = Math.max(...questions.map(q => q.id), 0);
    const additionalQuestions = ADDITIONAL_QUESTIONS_BY_LANGUAGE[userLanguage] || ADDITIONAL_QUESTIONS_BY_LANGUAGE.en;
    const newQuestions = additionalQuestions.map((q, idx) => ({
      ...q,
      id: currentMaxId + idx + 1
    }));
    setQuestions([...questions, ...newQuestions]);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Listen to My Life Story Podcast');
    const body = encodeURIComponent(`I've created a podcast sharing my life stories and memories. Listen to it here: ${window.location.origin}${podcastUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Listen to my life story podcast: ${window.location.origin}${podcastUrl}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${podcastUrl}`);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      alert('Failed to copy link');
    }
  };

  const downloadPodcast = () => {
    const link = document.createElement('a');
    link.href = `${API_URL}${podcastUrl}`;
    link.download = 'my-legacy-podcast.mp3';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const recordedCount = Object.keys(recordings).length;
  const totalQuestions = questions.length;
  const canCompile = recordedCount >= 2; // Minimum 2 questions

  // Show loading state while questions are being initialized
  if (!questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-purple-400 mb-4"></div>
          <p className="text-white text-lg">Loading your podcast studio...</p>
        </div>
      </div>
    );
  }

  // Share Modal
  if (showShareModal && podcastUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="h-12 w-12 text-green-400" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-2">🎉 Your Podcast is Ready!</h1>
              <p className="text-xl text-purple-200">
                Share your legacy with loved ones
              </p>
            </div>

            {/* Audio Player */}
            <div className="bg-white/5 rounded-2xl p-6 mb-8">
              {podcastUrl ? (
                <>
                  <p className="text-xs text-gray-400 mb-2">Audio URL: {`${API_URL}${podcastUrl}`}</p>
                  <audio 
                    controls 
                    src={`${API_URL}${podcastUrl}`}
                    className="w-full"
                    style={{filter: 'invert(1) hue-rotate(180deg)'}}
                    onError={(e) => {
                      console.error('Audio load error:', e);
                      console.error('Full URL:', `${API_URL}${podcastUrl}`);
                      console.error('API_URL:', API_URL);
                      console.error('podcastUrl:', podcastUrl);
                    }}
                  />
                </>
              ) : (
                <p className="text-white text-center">Loading podcast...</p>
              )}
            </div>

            {/* Share Options */}
            <div className="space-y-3 mb-8">
              <button
                onClick={shareViaEmail}
                className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-3"
              >
                <span className="text-2xl">📧</span>
                <span>Share via Email</span>
              </button>
              
              <button
                onClick={shareViaWhatsApp}
                className="w-full px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-3"
              >
                <span className="text-2xl">💬</span>
                <span>Share via WhatsApp</span>
              </button>
              
              <button
                onClick={copyShareLink}
                className="w-full px-6 py-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-3"
              >
                <span className="text-2xl">{copySuccess ? '✓' : '🔗'}</span>
                <span>{copySuccess ? 'Link Copied!' : 'Copy Share Link'}</span>
              </button>
              
              <button
                onClick={downloadPodcast}
                className="w-full px-6 py-4 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-3"
              >
                <span className="text-2xl">📥</span>
                <span>Download Podcast</span>
              </button>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => window.location.href = '/podcast-library'}
                className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
              >
                View Library
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
              >
                Create Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (customizing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8">
            <h1 className="text-3xl font-bold text-white mb-2">Customize Questions</h1>
            <p className="text-purple-200 mb-8">Edit existing questions or add your own</p>
            
            <div className="space-y-3 mb-6">
              {questions.map((q) => (
                <div key={q.id} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  {editingQuestionId === q.id ? (
                    <>
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                        autoFocus
                      />
                      <button
                        onClick={() => updateQuestion(q.id, editText)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingQuestionId(null);
                          setEditText('');
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-purple-300 font-semibold w-8">Q{q.id}</span>
                      <p className="flex-1 text-white">{q.text}</p>
                      <button
                        onClick={() => {
                          setEditingQuestionId(q.id);
                          setEditText(q.text);
                        }}
                        className="p-2 text-blue-400 hover:text-blue-300"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteQuestion(q.id)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-8 p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-sm text-purple-300 mb-2">Add Custom Question</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="Enter your question..."
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomQuestion()}
                />
                <button
                  onClick={addCustomQuestion}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  Add
                </button>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setCustomizing(false)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700"
              >
                Start Recording →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showReview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setShowReview(false)}
            className="mb-6 flex items-center text-purple-300 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Recording
          </button>
          
          <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8">
            <h1 className="text-3xl font-bold text-white mb-2">Review Your Podcast</h1>
            <p className="text-purple-200 mb-8">{recordedCount} of {totalQuestions} questions recorded</p>
            
            <div className="space-y-4 mb-8">
              {questions.map((question, index) => {
                const questionNumber = index + 1;
                const recording = recordings[questionNumber];
                
                return (
                  <div
                    key={question.id}
                    className={`p-4 rounded-xl border ${
                      recording
                        ? 'bg-emerald-900/20 border-emerald-500/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {recording ? (
                            <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-gray-500" />
                          )}
                          <span className="text-sm text-purple-300">Question {questionNumber}</span>
                        </div>
                        <p className="text-white">{question.text}</p>
                        {recording && (
                          <p className="text-sm text-emerald-400 mt-1">
                            Duration: {formatTime(recording.duration)}
                          </p>
                        )}
                      </div>
                      
                      {recording && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => playRecording(questionNumber)}
                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <PlayIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              setCurrentQuestionIndex(index);
                              setShowReview(false);
                            }}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            Re-record
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={compilePodcast}
                disabled={!canCompile || compiling}
                className="flex-1 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-lg font-semibold rounded-lg hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {compiling ? 'Compiling...' : '🎙️ Compile Podcast'}
              </button>
            </div>
            
            {!canCompile && (
              <p className="text-center text-yellow-400 mt-4 text-sm">
                Record at least 2 questions to compile your podcast
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const hasRecording = recordings[currentQuestionIndex + 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType="podcast_sessions"
        currentTier={subscription?.tier || 'free'}
        currentUsage={usage?.usage?.podcast_sessions?.current || 0}
        limit={usage?.usage?.podcast_sessions?.limit || 0}
      />

      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="w-80 bg-black/40 backdrop-blur-xl border-r border-white/10 p-6 overflow-y-auto">
          <h2 className="text-xl font-bold text-white mb-4">Questions</h2>
          <p className="text-sm text-purple-300 mb-6">{recordedCount} of {totalQuestions} recorded</p>
          
          <div className="space-y-2">
            {questions.map((question, index) => {
              const questionNumber = index + 1;
              const isRecorded = recordings[questionNumber];
              const isCurrent = index === currentQuestionIndex;
              
              return (
                <button
                  key={question.id}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isCurrent
                      ? 'bg-purple-600 text-white'
                      : isRecorded
                      ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {isRecorded ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-current" />
                    )}
                    <span className="text-xs font-semibold">Q{questionNumber}</span>
                  </div>
                  <p className="text-sm line-clamp-2">{question.text}</p>
                </button>
              );
            })}
          </div>
          
          <div className="mt-6 space-y-2">
            {questions.length === 2 && (
              <button
                onClick={addMoreQuestions}
                className="w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Add More Questions
              </button>
            )}
            <button
              onClick={() => setCustomizing(true)}
              className="w-full px-4 py-2 bg-white/5 text-purple-300 font-semibold rounded-lg hover:bg-white/10 border border-white/10"
            >
              ✏️ Edit Questions
            </button>
            <button
              onClick={() => setShowReview(true)}
              className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700"
            >
              Review & Compile →
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            <div className="bg-black/40 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 p-8">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                    <span className="text-white text-xl font-bold">AI</span>
                  </div>
                  <div>
                    <p className="text-sm text-purple-300">Question {currentQuestionIndex + 1} of {totalQuestions}</p>
                    <p className="text-xs text-purple-400">Max: {formatTime(currentQuestion.maxDuration)}</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-2xl p-6">
                  <p className="text-2xl md:text-3xl text-white font-light leading-relaxed">
                    {currentQuestion.text}
                  </p>
                </div>
              </div>

              {/* Recording Controls */}
              <div className="flex flex-col items-center space-y-6">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    className="group relative inline-flex items-center px-12 py-6 text-xl font-bold rounded-full shadow-2xl text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 transition-all transform hover:scale-105"
                  >
                    <div className="absolute inset-0 rounded-full bg-red-400 blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
                    <MicrophoneIcon className="relative h-10 w-10 mr-4" />
                    <span className="relative">{hasRecording ? 'Re-record' : 'Start Recording'}</span>
                  </button>
                ) : (
                  <div className="text-center">
                    <div className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-600 mb-4 animate-pulse">
                      {formatTime(timer)}
                    </div>
                    <div className="inline-flex items-center px-8 py-4 bg-red-500/20 border border-red-500/30 rounded-full mb-8">
                      <div className="h-4 w-4 bg-red-500 rounded-full animate-pulse mr-4"></div>
                      <span className="text-red-300 font-semibold text-lg">Recording...</span>
                    </div>
                    <button
                      onClick={stopRecording}
                      className="px-8 py-4 border-2 border-white/20 text-lg font-semibold rounded-full text-white bg-white/10 hover:bg-white/20"
                    >
                      <StopIcon className="h-6 w-6 inline mr-2" />
                      Stop & Save
                    </button>
                  </div>
                )}
              </div>

              {/* Current Recording Preview */}
              {hasRecording && !isRecording && (
                <div className="mt-8 p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-300 font-semibold">✓ Recorded</p>
                      <p className="text-xs text-emerald-400">Duration: {formatTime(hasRecording.duration)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => playRecording(currentQuestionIndex + 1)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <PlayIcon className="h-5 w-5 inline mr-1" />
                        Play
                      </button>
                      <button
                        onClick={() => deleteRecording(currentQuestionIndex + 1)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        <TrashIcon className="h-5 w-5 inline mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
